import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useTranslation } from 'react-i18next';
import { TestCaseInstanceFields } from './TestCaseInstanceFields';
import type { TestCaseInstance } from './types';

interface InstanceReviewDiffSectionProps {
  current: TestCaseInstance | null;
  pending: TestCaseInstance[];
  selectedPendingId: string | null;
  processing: boolean;
  currentAction: 'accept' | 'reject' | 'regenerate' | null;
  onSelectPending: (instanceId: string) => void;
  onAccept: () => void;
  onReject: () => void;
  onRegenerate: () => void;
}

const useStyles = makeStyles(theme => ({
  pendingSelector: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  instanceCard: {
    height: '100%',
  },
  currentCard: {
    borderTop: `4px solid ${theme.palette.primary.main}`,
  },
  pendingCard: {
    borderTop: `4px solid ${theme.palette.warning.main}`,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },
  instanceTitle: {
    fontWeight: 600,
  },
  badges: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  sourceChip: {
    backgroundColor: 'transparent',
  },
  pendingStatusChip: {
    color: theme.palette.warning.dark,
    backgroundColor: theme.palette.warning.light,
  },
  feedback: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  feedbackLabel: {
    display: 'block',
    marginBottom: theme.spacing(0.5),
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(3),
  },
}));

function formatDate(value: string): string {
  if (!value) {
    return '—';
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export const InstanceReviewDiffSection = ({
  current,
  pending,
  selectedPendingId,
  processing,
  currentAction,
  onSelectPending,
  onAccept,
  onReject,
  onRegenerate,
}: InstanceReviewDiffSectionProps) => {
  const classes = useStyles();
  const { t } = useTranslation('testCases');

  const selectedPending =
    pending.find(instance => instance.instanceId === selectedPendingId) ?? null;

  if (!current) {
    return (
      <Alert severity="error">
        {t('testCaseGeneration.instanceReview.noCurrent')}
      </Alert>
    );
  }

  if (!selectedPending) {
    return (
      <Alert severity="info">
        {t('testCaseGeneration.instanceReview.noPending')}
      </Alert>
    );
  }

  return (
    <>
      {pending.length > 1 && (
        <Box className={classes.pendingSelector}>
          <Typography variant="body2">
            {t('testCaseGeneration.instanceReview.pendingProposal')}
          </Typography>

          {pending.map(instance => (
            <Button
              key={instance.instanceId}
              size="small"
              color="primary"
              variant={
                instance.instanceId === selectedPendingId
                  ? 'contained'
                  : 'outlined'
              }
              disabled={processing}
              onClick={() => onSelectPending(instance.instanceId)}
            >
              {t('testCaseGeneration.instanceReview.versionSource', {
                number: instance.versionNumber,
                source: instance.source,
              })}
            </Button>
          ))}
        </Box>
      )}

      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={6}>
          <Card
            variant="outlined"
            className={`${classes.instanceCard} ${classes.currentCard}`}
          >
            <CardContent>
              <Box className={classes.header}>
                <Box>
                  <Typography
                    variant="subtitle1"
                    className={classes.instanceTitle}
                  >
                    {t('testCaseGeneration.instanceReview.currentInstance')}
                  </Typography>

                  <Typography variant="body2" color="textSecondary">
                    {t('testCaseGeneration.instanceReview.versionMetadata', {
                      number: current.versionNumber,
                      timestamp: formatDate(current.createdAt),
                    })}
                  </Typography>
                </Box>

                <Box className={classes.badges}>
                  <Chip
                    size="small"
                    color="primary"
                    label={t(
                      'testCaseGeneration.instanceReview.status.current',
                    )}
                  />

                  <Chip
                    size="small"
                    variant="outlined"
                    className={classes.sourceChip}
                    label={current.source}
                  />
                </Box>
              </Box>

              <TestCaseInstanceFields fields={current.fields} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card
            variant="outlined"
            className={`${classes.instanceCard} ${classes.pendingCard}`}
          >
            <CardContent>
              <Box className={classes.header}>
                <Box>
                  <Typography
                    variant="subtitle1"
                    className={classes.instanceTitle}
                  >
                    {t('testCaseGeneration.instanceReview.proposedInstance')}
                  </Typography>

                  <Typography variant="body2" color="textSecondary">
                    {t('testCaseGeneration.instanceReview.versionMetadata', {
                      number: selectedPending.versionNumber,
                      timestamp: formatDate(selectedPending.createdAt),
                    })}
                  </Typography>
                </Box>

                <Box className={classes.badges}>
                  <Chip
                    size="small"
                    className={classes.pendingStatusChip}
                    label={t(
                      'testCaseGeneration.instanceReview.status.pendingReview',
                    )}
                  />

                  <Chip
                    size="small"
                    variant="outlined"
                    className={classes.sourceChip}
                    label={selectedPending.source}
                  />
                </Box>
              </Box>

              {selectedPending.feedbackInstruction && (
                <Box className={classes.feedback}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    className={classes.feedbackLabel}
                  >
                    {t('testCaseGeneration.instanceReview.proposalInstruction')}
                  </Typography>

                  <Typography variant="body2">
                    {selectedPending.feedbackInstruction}
                  </Typography>
                </Box>
              )}

              <TestCaseInstanceFields fields={selectedPending.fields} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {processing && currentAction === 'regenerate' && (
        <Box mt={2}>
          <Alert severity="info">
            {t(
              'testCaseGeneration.instanceReview.messages.regenerationInProgress',
            )}
          </Alert>
        </Box>
      )}

      <Box className={classes.actions}>
        <Button
          variant="outlined"
          color="secondary"
          disabled={processing}
          onClick={onReject}
          startIcon={
            processing && currentAction === 'reject' ? (
              <CircularProgress size={16} />
            ) : undefined
          }
        >
          {t('testCaseGeneration.instanceReview.reject')}
        </Button>

        <Button
          variant="outlined"
          disabled={processing}
          onClick={onRegenerate}
          startIcon={
            processing && currentAction === 'regenerate' ? (
              <CircularProgress size={16} />
            ) : undefined
          }
        >
          {processing && currentAction === 'regenerate'
            ? t('testCaseGeneration.instanceReview.regenerating')
            : t('testCaseGeneration.instanceReview.regenerate')}
        </Button>

        <Button
          variant="contained"
          color="primary"
          disabled={processing}
          onClick={onAccept}
          startIcon={
            processing && currentAction === 'accept' ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {t('testCaseGeneration.instanceReview.accept')}
        </Button>
      </Box>
    </>
  );
};

export default InstanceReviewDiffSection;
